import {
  useIsFeatureEnabled,
  usePermissionsForCurrentUser,
} from '@retail/app/stores/selected-context';
import { CalculusTab } from '@retail/calculus/types';
import { useMemo } from 'react';
import { Resource } from '@retail/auth/types';
import { TypedObject } from '@shared/utils';

const calculusAreaResourceMap: Record<CalculusTab, Resource> = {
  [CalculusTab.ITEMS]: 'PRICE_CALCULATION__ITEM_PRICES',
  [CalculusTab.PURCHASE]: 'PRICE_CALCULATION__PURCHASE_CONDITION',
  [CalculusTab.FREIGHT]: 'PRICE_CALCULATION__FREIGHT',
  [CalculusTab.MARKUP]: 'PRICE_CALCULATION__MARKUP',
  [CalculusTab.PRICE_SUGGESTION]: 'PRICE_CALCULATION__PRICE_SUGGESTIONS',
  [CalculusTab.CUSTOMER_MARKUP]: 'PRICE_CALCULATION__CUSTOMER_MARKUP',
  [CalculusTab.SALES_PRICES]: 'PRICE_CALCULATION__SALES_PRICE_CONDITION',
  [CalculusTab.ROUNDING]: 'PRICE_CALCULATION__ROUNDING',
  [CalculusTab.PRICE_SOURCE]: 'PRICE_CALCULATION__SALES_PRICE_CONDITION',
  [CalculusTab.ALL_CONDITIONS]: 'PRICE_CALCULATION__COMBINED',
};

export const useAvailableCalculusAreas = (): CalculusTab[] => {
  const { userPermissions } = usePermissionsForCurrentUser();
  const salesPriceConditionIsEnabled = useIsFeatureEnabled('sales-price-condition');
  const roundingIsEnabled = useIsFeatureEnabled('price-rounding');
  const allConditionsIsEnabled = useIsFeatureEnabled('all-conditions');
  const discountMatrixIsEnabled = useIsFeatureEnabled('discount-matrix');

  const featureToggledCalculusAreas: Record<CalculusTab, boolean> = useMemo(
    () => ({
      [CalculusTab.ITEMS]: true,
      [CalculusTab.PURCHASE]: true,
      [CalculusTab.FREIGHT]: true,
      [CalculusTab.MARKUP]: !salesPriceConditionIsEnabled,
      [CalculusTab.PRICE_SUGGESTION]: !salesPriceConditionIsEnabled,
      [CalculusTab.CUSTOMER_MARKUP]: !discountMatrixIsEnabled,
      [CalculusTab.ROUNDING]: roundingIsEnabled,
      [CalculusTab.SALES_PRICES]: salesPriceConditionIsEnabled,
      [CalculusTab.PRICE_SOURCE]: true,
      [CalculusTab.ALL_CONDITIONS]: allConditionsIsEnabled,
    }),
    [
      roundingIsEnabled,
      salesPriceConditionIsEnabled,
      allConditionsIsEnabled,
      discountMatrixIsEnabled,
    ]
  );

  return useMemo(
    () =>
      TypedObject.entries(calculusAreaResourceMap)
        .filter(([value]) => featureToggledCalculusAreas[value])
        .filter(([_, resource]) =>
          userPermissions.some(
            (permission) => permission.resource === resource && permission.canRead()
          )
        )
        .map(([area]) => area as CalculusTab),
    [featureToggledCalculusAreas, userPermissions]
  );
};
